import { useMemo } from 'react';
import { useRbac } from '../contexts/RbacContext';
import { useResolvedResource } from '../contexts/useResourceContext';
import { RBAC_PERMISSIONS } from '../config/rbac';

export interface TableRbacResult {
  resource: string | null;
  canRead: boolean;
  canWrite: boolean;
  canDelete: boolean;
  canManage: boolean;
  isReadOnly: boolean;
  canDeleteRows: boolean;
  isLoading: boolean;
  hasResource: boolean;
}

export function useTableRbac(explicitResource?: string): TableRbacResult {
  const resource = useResolvedResource(explicitResource);
  const { hasPermission, isLoading } = useRbac();

  return useMemo(() => {
    if (!resource) {
      return {
        resource: null,
        canRead: false,
        canWrite: false,
        canDelete: false,
        canManage: false,
        isReadOnly: true,
        canDeleteRows: false,
        isLoading,
        hasResource: false,
      };
    }

    const canRead = hasPermission(resource, RBAC_PERMISSIONS.READ);
    const canWrite = hasPermission(resource, RBAC_PERMISSIONS.WRITE);
    const canDelete = hasPermission(resource, RBAC_PERMISSIONS.DELETE);
    const canManage = hasPermission(resource, RBAC_PERMISSIONS.MANAGE);

    return {
      resource,
      canRead,
      canWrite,
      canDelete,
      canManage,
      isReadOnly: !canWrite,
      canDeleteRows: canDelete,
      isLoading,
      hasResource: true,
    };
  }, [resource, hasPermission, isLoading]);
}

type ContextMenuType = boolean | string[] | object | undefined;

export function useFilteredContextMenu(
  contextMenu: ContextMenuType,
  rbac: TableRbacResult
): ContextMenuType {
  return useMemo(() => {
    if (!Array.isArray(contextMenu)) {
      return contextMenu;
    }

    return contextMenu.filter((item) => {
      if (typeof item === 'string') {
        if (item === 'remove_row' && !rbac.canDeleteRows) {
          return false;
        }
        if ((item === 'cut' || item === 'undo' || item === 'redo') && rbac.isReadOnly) {
          return false;
        }
      }

      if (typeof item === 'object' && item !== null && 'key' in item) {
        const key = (item as { key: string }).key;
        if (key === 'remove_row' && !rbac.canDeleteRows) {
          return false;
        }
        if ((key === 'cut' || key === 'undo' || key === 'redo') && rbac.isReadOnly) {
          return false;
        }
      }

      return true;
    });
  }, [contextMenu, rbac.canDeleteRows, rbac.isReadOnly]);
}


